/**
 * EditorMenu — a small accessible popover menu for the sitemap editor.
 *
 * Isolated UI primitive (react-strict-dom) so call sites stay free of ad-hoc
 * popover markup. Not Duro's `Menu`: that one opens from its own Trigger and
 * has no disabled / danger items, while this one opens at a point (a
 * contextmenu event) and needs both. Encapsulates the menu accessibility contract: `role="menu"`
 * / `role="menuitem"`, focus the first item on open, Escape + outside-pointer
 * close, ArrowUp/Down roving focus, and viewport-collision clamping.
 *
 * Positioned `fixed` at viewport coords (x,y) — the caller passes the anchor's
 * client position (e.g. a contextmenu event or a button's getBoundingClientRect).
 *
 * holds-until: Duro Menu opens at a point with disabled and danger items (https://github.com/fredericrous/duro-design-system/issues/103); until then this menu stays html.* with tokens.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  spacing,
  microSpacing,
  radii,
} from "@duro-app/tokens/tokens/spacing.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typeScale } from "@duro-app/tokens/tokens/typography.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

export interface EditorMenuProps {
  /** Anchor position in viewport (client) coordinates. */
  readonly x: number;
  readonly y: number;
  readonly onClose: () => void;
  readonly ariaLabel?: string;
  readonly children: ReactNode;
}

const MARGIN = 8;

export function EditorMenu({
  x,
  y,
  onClose,
  ariaLabel,
  children,
}: EditorMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Keep the menu inside the viewport (flip/clamp after measuring).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + r.width > window.innerWidth - MARGIN)
      left = Math.max(MARGIN, window.innerWidth - r.width - MARGIN);
    if (top + r.height > window.innerHeight - MARGIN)
      top = Math.max(MARGIN, window.innerHeight - r.height - MARGIN);
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = () =>
      Array.from(
        el.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([aria-disabled="true"])',
        ),
      );
    items()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const list = items();
        if (list.length === 0) return;
        const idx = list.indexOf(document.activeElement as HTMLElement);
        const next =
          e.key === "ArrowDown"
            ? list[(idx + 1) % list.length]
            : list[(idx - 1 + list.length) % list.length];
        next?.focus();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (!el.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [onClose]);

  return (
    <html.div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
      style={[s.menu, s.at(pos.left, pos.top)]}
    >
      {children}
    </html.div>
  );
}

export interface EditorMenuItemProps {
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly danger?: boolean;
  readonly children: ReactNode;
}

export function EditorMenuItem({
  onSelect,
  disabled,
  danger,
  children,
}: EditorMenuItemProps) {
  return (
    <html.button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      style={[s.item, danger && s.itemDanger, disabled && s.itemDisabled]}
    >
      {children}
    </html.button>
  );
}

const s = css.create({
  menu: {
    position: "fixed",
    zIndex: layers.overlay,
    minWidth: sizes.timelineLabelW,
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
    boxShadow: shadows.lg,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xs,
    paddingLeft: spacing.xs,
    paddingRight: spacing.xs,
    display: "flex",
    flexDirection: "column",
    gap: microSpacing.px1,
  },
  at: (left: number, top: number) => ({ left, top }),
  item: {
    textAlign: "left",
    borderStyle: "none",
    backgroundColor: { default: "transparent", ":hover": colors.bgCardHover },
    borderRadius: radii.px6,
    paddingTop: microSpacing.px6,
    paddingBottom: microSpacing.px6,
    // 0.6rem (9.6px) has no spacing token within ±10% (sm 8 / ms 12): left as
    // the authored rem until the scale has one.
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    fontSize: typeScale.fontSize2,
    color: colors.text,
    cursor: "pointer",
  },
  itemDanger: { color: colors.error },
  itemDisabled: {
    color: colors.textMuted,
    cursor: "default",
    backgroundColor: "transparent",
  },
});
